/**
 * CheckoutButton Component
 * Initiates Stripe checkout session with reCAPTCHA protection
 */

import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useCartStore, useCartItemsWithDetails } from '../../store/cart';
import { trackCheckoutStart } from '../../lib/analytics';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import api from '../../services/api';

interface CheckoutButtonProps {
  disabled?: boolean;
  email?: string;
  company?: string;
}

const CheckoutButton: React.FC<CheckoutButtonProps> = ({
  disabled = false,
  email,
  company,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const items = useCartItemsWithDetails();
  const { getTotal } = useCartStore();
  const { executeRecaptcha } = useRecaptcha();

  const handleCheckout = async () => {
    if (items.length === 0) {
      setError('Your cart is empty');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Execute reCAPTCHA verification
      const recaptchaToken = await executeRecaptcha('CHECKOUT');
      if (!recaptchaToken) {
        throw new Error('Security verification failed. Please try again.');
      }

      // Track checkout start
      trackCheckoutStart(
        items.map(i => ({
          skuId: i.skuId,
          quantity: i.qty,
          price: i.sku.unitAmount,
        })),
        getTotal()
      );

      // Create checkout session
      const response = await api.post('/checkout/create-session', {
        items: items.map(item => ({
          skuId: item.skuId,
          qty: item.qty,
        })),
        email,
        company,
        recaptchaToken,
      });

      if (response.data.success && response.data.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.url;
      } else if (response.data.data.bridgeUrl) {
        // Mixed cart - redirect to bridge page
        window.location.href = response.data.data.bridgeUrl;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(
        err.response?.data?.error?.message ||
        err.message ||
        'Failed to start checkout. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleCheckout}
        disabled={disabled || loading || items.length === 0}
        startIcon={
          loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <CreditCardIcon />
          )
        }
        sx={{
          py: 1.5,
          fontSize: '1.1rem',
        }}
      >
        {loading ? 'Processing...' : 'Complete Purchase'}
      </Button>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          mt: 1.5,
        }}
      >
        <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          Secured by Stripe • 256-bit encryption
        </Typography>
      </Box>
    </Box>
  );
};

export default CheckoutButton;
